import { useState, useEffect } from "react";
import { api, handleAuthFailure } from "../data/api";

const PAGES = [
  ["dashboard",  "Dashboard"],
  ["affiliates", "Affiliates"],
  ["channels",   "Channels"],
  ["devices",    "Devices"],
  ["ota",        "OTA Updates"],
  ["logs",       "Log Viewer"],
  ["activity",   "Activity Log"],
];

const EMPTY_PERMS = {
  ...PAGES.reduce((acc, [key]) => ({ ...acc, [key]: "none" }), {}),
  dashboard: "view",
};

function PermissionGrid({ value, onChange, disabled }) {
  return (
    <div style={{ border: `1px solid var(--border)`, borderRadius: 8, overflow: "hidden" }}>
      {PAGES.map(([key, label], i) => (
        <div
          key={key}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "9px 12px",
            borderTop: i === 0 ? "none" : `1px solid var(--border)`,
            background: "var(--surface)",
          }}
        >
          <span style={{ fontSize: 12, color: "var(--ink)", fontWeight: 500 }}>{label}</span>
          <div style={{ display: "flex", gap: 4 }}>
            {(key === "dashboard" ? ["view", "edit"] : ["none", "view", "edit"]).map((level) => (
              <button
                key={level}
                type="button"
                disabled={disabled}
                onClick={() => onChange(key, level)}
                style={{
                  padding: "4px 11px",
                  borderRadius: 5,
                  border: `1px solid ${value[key] === level ? "var(--ink)" : "var(--border)"}`,
                  background: value[key] === level ? "var(--ink)" : "var(--surface)",
                  color: value[key] === level ? "#fff" : "var(--text-sub)",
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: "capitalize",
                  cursor: disabled ? "default" : "pointer",
                  fontFamily: "inherit",
                }}
              >
                {level}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function UsersManagement() {
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const [showNew, setShowNew]   = useState(false);
  const [form, setForm]         = useState({ email: "", password: "", name: "", permissions: EMPTY_PERMS });
  const [saving, setSaving]     = useState(false);
  const [formError, setFormError] = useState(null);

  const [editingId, setEditingId]     = useState(null);
  const [editPerms, setEditPerms]     = useState(EMPTY_PERMS);
  const [editPassword, setEditPassword] = useState("");
  const [deletingId, setDeletingId]   = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const rows = await api.users.list();
      setUsers(Array.isArray(rows) ? rows : []);
      setError(null);
    } catch (e) {
      if (handleAuthFailure(e)) return;
      setError(e.message || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    setFormError(null);
    if (!form.email.trim() || !form.password.trim()) {
      setFormError("Email and password are required");
      return;
    }
    setSaving(true);
    try {
      await api.users.create(form);
      setShowNew(false);
      setForm({ email: "", password: "", name: "", permissions: EMPTY_PERMS });
      await load();
    } catch (e) {
      setFormError(e.message || "Failed to create user");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (u) => {
    setEditingId(u.id);
    setEditPerms(u.permissions || EMPTY_PERMS);
    setEditPassword("");
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const body = { permissions: editPerms };
      if (editPassword.trim()) body.password = editPassword.trim();
      await api.users.update(editingId, body);
      setEditingId(null);
      await load();
    } catch (e) {
      setFormError(e.message || "Failed to update user");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    await api.users.delete(id);
    setDeletingId(null);
    await load();
  };

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 300, fontSize: 12, color: "var(--text-dim)" }}>
      Loading users…
    </div>
  );

  return (
    <div style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, paddingBottom: 14, borderBottom: `1px solid var(--border)` }}>
        <div>
          <h1 style={{ fontSize: 17, fontWeight: 700, color: "var(--ink)" }}>User Management</h1>
          <div style={{ fontSize: 12, color: "var(--text-sub)", marginTop: 2 }}>
            Create restricted logins with per-page View / Edit access.
          </div>
        </div>
        <button
          onClick={() => { setShowNew(true); setFormError(null); }}
          style={{ background: "var(--ink)", border: "none", borderRadius: 6, padding: "7px 14px", fontSize: 12, fontWeight: 600, color: "#fff", cursor: "pointer", fontFamily: "inherit" }}
        >
          + New User
        </button>
      </div>

      {error && (
        <div style={{ background: "#fdf0f0", border: "1px solid #e8b4b4", borderRadius: 7, padding: "10px 14px", fontSize: 12, color: "#8b1f1f", marginBottom: 14 }}>
          {error}
        </div>
      )}

      {/* New User modal */}
      {showNew && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}>
          <div style={{ background: "var(--bg)", borderRadius: 10, padding: 22, width: 480, maxHeight: "85vh", overflowY: "auto" }}>
            <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 14, color: "var(--ink)" }}>New Restricted User</h2>

            {formError && (
              <div style={{ background: "#fdf0f0", border: "1px solid #e8b4b4", borderRadius: 6, padding: "8px 12px", fontSize: 12, color: "#8b1f1f", marginBottom: 12 }}>
                {formError}
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 11, color: "var(--text-dim)", fontWeight: 600, marginBottom: 4 }}>Email *</div>
                <input
                  value={form.email}
                  onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  placeholder="user@example.com"
                  style={{ width: "100%", boxSizing: "border-box", background: "var(--surface)", border: `1px solid var(--border)`, borderRadius: 6, padding: "8px 10px", fontSize: 12, color: "var(--text)", fontFamily: "inherit", outline: "none" }}
                />
              </div>
              <div>
                <div style={{ fontSize: 11, color: "var(--text-dim)", fontWeight: 600, marginBottom: 4 }}>Password *</div>
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                  placeholder="••••••••"
                  style={{ width: "100%", boxSizing: "border-box", background: "var(--surface)", border: `1px solid var(--border)`, borderRadius: 6, padding: "8px 10px", fontSize: 12, color: "var(--text)", fontFamily: "inherit", outline: "none" }}
                />
              </div>
              <div>
                <div style={{ fontSize: 11, color: "var(--text-dim)", fontWeight: 600, marginBottom: 4 }}>Name</div>
                <input
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Jane Smith"
                  style={{ width: "100%", boxSizing: "border-box", background: "var(--surface)", border: `1px solid var(--border)`, borderRadius: 6, padding: "8px 10px", fontSize: 12, color: "var(--text)", fontFamily: "inherit", outline: "none" }}
                />
              </div>
            </div>

            <div style={{ fontSize: 11, color: "var(--text-dim)", fontWeight: 600, marginBottom: 6 }}>Page Access</div>
            <PermissionGrid
              value={form.permissions}
              onChange={(key, level) => setForm((p) => ({ ...p, permissions: { ...p.permissions, [key]: level } }))}
            />

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
              <button onClick={() => setShowNew(false)} style={{ background: "var(--surface)", border: `1px solid var(--border)`, borderRadius: 6, padding: "7px 14px", fontSize: 12, color: "var(--text-sub)", cursor: "pointer", fontFamily: "inherit" }}>
                Cancel
              </button>
              <button onClick={handleCreate} disabled={saving} style={{ background: "var(--ink)", border: "none", borderRadius: 6, padding: "7px 14px", fontSize: 12, fontWeight: 600, color: "#fff", cursor: saving ? "default" : "pointer", opacity: saving ? 0.6 : 1, fontFamily: "inherit" }}>
                {saving ? "Creating…" : "Create User"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit permissions modal */}
      {editingId && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 }}>
          <div style={{ background: "var(--bg)", borderRadius: 10, padding: 22, width: 480, maxHeight: "85vh", overflowY: "auto" }}>
            <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 14, color: "var(--ink)" }}>
              Edit Access — {users.find(u => u.id === editingId)?.email}
            </h2>

            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: "var(--text-dim)", fontWeight: 600, marginBottom: 4 }}>Reset Password (optional)</div>
              <input
                type="password"
                value={editPassword}
                onChange={(e) => setEditPassword(e.target.value)}
                placeholder="Leave blank to keep current password"
                style={{ width: "100%", boxSizing: "border-box", background: "var(--surface)", border: `1px solid var(--border)`, borderRadius: 6, padding: "8px 10px", fontSize: 12, color: "var(--text)", fontFamily: "inherit", outline: "none" }}
              />
            </div>

            <div style={{ fontSize: 11, color: "var(--text-dim)", fontWeight: 600, marginBottom: 6 }}>Page Access</div>
            <PermissionGrid
              value={editPerms}
              onChange={(key, level) => setEditPerms((p) => ({ ...p, [key]: level }))}
            />

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
              <button onClick={() => setEditingId(null)} style={{ background: "var(--surface)", border: `1px solid var(--border)`, borderRadius: 6, padding: "7px 14px", fontSize: 12, color: "var(--text-sub)", cursor: "pointer", fontFamily: "inherit" }}>
                Cancel
              </button>
              <button onClick={saveEdit} disabled={saving} style={{ background: "var(--ink)", border: "none", borderRadius: 6, padding: "7px 14px", fontSize: 12, fontWeight: 600, color: "#fff", cursor: saving ? "default" : "pointer", opacity: saving ? 0.6 : 1, fontFamily: "inherit" }}>
                {saving ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User list */}
      <div style={{ border: `1px solid var(--border)`, borderRadius: 8, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "var(--surface-alt)" }}>
              {["Email", "Name", "Role", "Access Summary", ""].map((h) => (
                <th key={h} style={{ textAlign: "left", padding: "9px 12px", fontSize: 11, fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: `1px solid var(--border)` }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const grantedPages = u.role === "custom"
                ? PAGES.filter(([key]) => (u.permissions?.[key] || "none") !== "none")
                : [];
              return (
                <tr key={u.id} style={{ borderBottom: `1px solid var(--border)` }}>
                  <td style={{ padding: "9px 12px", fontSize: 12, color: "var(--ink)" }}>{u.email}</td>
                  <td style={{ padding: "9px 12px", fontSize: 12, color: "var(--text-sub)" }}>{u.name || "—"}</td>
                  <td style={{ padding: "9px 12px", fontSize: 11 }}>
                    <span style={{
                      padding: "2px 8px", borderRadius: 5, fontWeight: 700, textTransform: "uppercase",
                      background: "var(--surface-alt)", border: `1px solid var(--border)`, color: "var(--text-sub)",
                    }}>
                      {u.role}
                    </span>
                  </td>
                  <td style={{ padding: "9px 12px", fontSize: 11, color: "var(--text-sub)" }}>
                    {u.role !== "custom"
                      ? "—"
                      : grantedPages.length
                        ? grantedPages.map(([key, label]) => `${label}: ${u.permissions[key]}`).join(", ")
                        : "No access granted"}
                  </td>
                  <td style={{ padding: "9px 12px", textAlign: "right" }}>
                    {u.role === "custom" && (
                      <>
                        <button onClick={() => startEdit(u)} style={{ background: "var(--surface)", border: `1px solid var(--border)`, borderRadius: 5, padding: "4px 10px", fontSize: 11, color: "var(--text-sub)", cursor: "pointer", fontFamily: "inherit", marginRight: 6 }}>
                          Edit
                        </button>
                        {deletingId === u.id ? (
                          <>
                            <button onClick={() => handleDelete(u.id)} style={{ background: "#c0392b", border: "none", borderRadius: 5, padding: "4px 10px", fontSize: 11, color: "#fff", cursor: "pointer", fontFamily: "inherit", marginRight: 4 }}>
                              Confirm
                            </button>
                            <button onClick={() => setDeletingId(null)} style={{ background: "var(--surface)", border: `1px solid var(--border)`, borderRadius: 5, padding: "4px 10px", fontSize: 11, color: "var(--text-sub)", cursor: "pointer", fontFamily: "inherit" }}>
                              Cancel
                            </button>
                          </>
                        ) : (
                          <button onClick={() => setDeletingId(u.id)} style={{ background: "var(--surface)", border: "1px solid #e8b4b4", borderRadius: 5, padding: "4px 10px", fontSize: 11, color: "#8b1f1f", cursor: "pointer", fontFamily: "inherit" }}>
                            Delete
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
            {!users.length && (
              <tr>
                <td colSpan={5} style={{ padding: "20px 12px", textAlign: "center", fontSize: 12, color: "var(--text-dim)" }}>
                  No users yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}